import { NAME_MAX_LENGTH } from '@cube-rush/shared';
import type { TargetedSubmitEvent } from 'preact';
import { useId, useRef, useState } from 'preact/hooks';
import type { LoginError } from '../../app/AppController';
import { useApp } from '../../app/context';
import { ru } from '../../i18n/ru';
import { Button } from '../components/Button';
import { Logo } from '../components/Logo';
import './LoginScreen.css';

/** Name + shared password. Shown only to players without a valid stored session. */
export function LoginScreen() {
  const app = useApp();
  const passwordId = useId();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(() => app.session.profile.peek()?.name ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  const notice = app.ui.loginNotice.value;
  const message = error ? ru.login.errors[error] : notice;

  const submit = async (event: TargetedSubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const problem = await app.login(password, name);
    // On success this screen is replaced, so only failures need local state.
    if (problem) {
      setError(problem);
      setBusy(false);
    }
  };

  return (
    <main class="screen login">
      <div class="login__hero">
        <Logo />
        <p class="login__tagline">{ru.tagline}</p>
      </div>
      <form class="login__form panel" onSubmit={submit} noValidate>
        <label class="field">
          <span class="field__label">{ru.login.nameLabel}</span>
          <input
            class="field__input"
            name="name"
            value={name}
            maxLength={NAME_MAX_LENGTH}
            placeholder={ru.login.namePlaceholder}
            autoComplete="nickname"
            autoCapitalize="words"
            enterKeyHint="next"
            spellcheck={false}
            onInput={(e) => setName(e.currentTarget.value)}
            onKeyDown={(e) => {
              // The keyboard's "next" key sends Enter: go on to the password instead of
              // submitting the form without it.
              if (e.key === 'Enter' && password === '') {
                e.preventDefault();
                passwordRef.current?.focus();
              }
            }}
          />
        </label>
        <div class="field">
          {/* The label is not wrapped around the row: the peek button must not become part of its name. */}
          <label class="field__label" for={passwordId}>
            {ru.login.passwordLabel}
          </label>
          <span class="field__row">
            <input
              ref={passwordRef}
              id={passwordId}
              class="field__input login__password"
              name="password"
              // Spread: Preact's input typings need each `type` literal in its own object.
              {...(showPassword ? { type: 'text' } : { type: 'password' })}
              value={password}
              placeholder={ru.login.passwordPlaceholder}
              autoComplete="current-password"
              autoCapitalize="none"
              enterKeyHint="go"
              spellcheck={false}
              onInput={(e) => setPassword(e.currentTarget.value)}
            />
            <button
              type="button"
              class="login__peek"
              aria-label={showPassword ? ru.login.hidePassword : ru.login.showPassword}
              onClick={() => setShowPassword((shown) => !shown)}
            >
              {showPassword ? '🙈' : '👀'}
            </button>
          </span>
          {/* Shown on computers only (LoginScreen.css). */}
          <p class="login__hint">{ru.login.passwordHint}</p>
        </div>
        {message && (
          <p class={`login__message${error ? ' login__message--error' : ''}`} role="alert">
            {message}
          </p>
        )}
        <Button type="submit" variant="green" size="big" block disabled={busy}>
          {busy ? ru.login.submitting : ru.login.submit}
        </Button>
      </form>
    </main>
  );
}
